import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../utils/api";

const inputClass =
  "w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200";

// Lets admins add a translation for each static word/phrase printed on the
// customer receipt (Item, Qty, Grand Total, etc). English is always the
// fallback - a blank translation just means that label stays English-only,
// same convention as item/add-on names elsewhere in the app.
const ReceiptLabelsSection = () => {
  const [languages, setLanguages] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [values, setValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const [langRes, labelsRes] = await Promise.all([
          api.get("/languages/availableLanguages"),
          api.get("/settings/receipt-labels"),
        ]);
        setLanguages(
          (Array.isArray(langRes.data) ? langRes.data : []).filter(
            (lang) => lang.code !== "en",
          ),
        );
        setDefaults(labelsRes.data?.defaults || {});
        setValues(labelsRes.data?.labels || {});
      } catch (err) {
        console.error("Failed loading receipt labels", err);
        toast.error("Failed to load receipt label translations");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (key, langCode, text) => {
    setValues((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [langCode]: text },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put("/settings/receipt-labels", { labels: values });
      toast.success("Receipt label translations saved");
    } catch (err) {
      console.error("Failed to save receipt labels", err);
      toast.error(
        err.response?.data?.message ||
          "Failed to save receipt label translations",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const keys = Object.keys(defaults);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-2 pb-4 border-b border-gray-100 mb-4">
        <div className="w-7 h-7 rounded-lg bg-black flex items-center justify-center text-white flex-shrink-0">
          <span className="text-xs font-semibold">4</span>
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-gray-900">
            Receipt Label Translations
          </h2>
          <p className="text-xs text-gray-500">
            Add a translation for the words printed on the customer receipt
            (Item, Qty, Grand Total, etc). It shows as "English /
            Translation" - leave a field blank to keep that label
            English-only.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
          <p className="text-sm font-semibold text-gray-700">Loading...</p>
        </div>
      ) : languages.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 p-8 text-center">
          <p className="text-sm font-semibold text-gray-700">
            No other languages configured
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Add a language under Languages settings to translate receipt
            labels.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {keys.map((key) => (
              <div
                key={key}
                className="rounded-2xl border border-gray-100 bg-white shadow-sm px-4 py-3"
              >
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                  {defaults[key]}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {languages.map((lang) => (
                    <div key={lang.code}>
                      <label className="block text-[11px] font-medium text-gray-500 mb-1">
                        {lang.name}
                      </label>
                      <input
                        type="text"
                        value={values[key]?.[lang.code] || ""}
                        onChange={(e) =>
                          handleChange(key, lang.code, e.target.value)
                        }
                        dir={lang.direction || "ltr"}
                        placeholder={defaults[key]}
                        className={inputClass}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className={`bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-xs font-semibold active:scale-[0.98] transition-all duration-200 ${
                isSaving ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isSaving ? "Saving..." : "Save Translations"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ReceiptLabelsSection;
